// Public-API notification envelope parsing + adaptation to the client storage
// shape. Mirrors Android's PublicApiCryptoOpener + PublicApiRenderer (see
// apps/android/.../publicapi/) so server-origin notifications surface
// identically across all three surfaces (Android, extension, web).
//
// Crypto is intentionally NOT performed here — consumers call decryptMessage
// from @reflecto/core themselves and pass the plaintext bytes. Keeps the
// crypto/wire-format split between @reflecto/core and @reflecto/client-core
// intact (same convention as api/devices.ts).

import type { DecryptedNotification } from "../storage-schema";

export type PublicSendPriority = "min" | "low" | "default" | "high" | "urgent";

export interface PublicSendAction {
  label: string;
  url: string;
}

export interface PublicSendPayload {
  message: string;
  title?: string;
  priority: PublicSendPriority;
  tags: string[];
  url?: string;
  url_title?: string;
  actions: PublicSendAction[];
  markdown: boolean;
  ttl: number;
}

export interface PublicSendSource {
  kind: "api";
  token_id: string;
  label: string;
  received_at: number;
}

export interface PublicSendEnvelope {
  type: "notification";
  payload: PublicSendPayload;
  source: PublicSendSource;
}

export type ServerPublicKeyRejectReason = "invalid_base64" | "wrong_byte_length";

export type ServerPublicKeyValidation =
  | { ok: true; value: string }
  | { ok: false; reason: ServerPublicKeyRejectReason; length: number };

/**
 * Inspect a base64-encoded X25519 public key. Returns a discriminated result
 * carrying the rejection reason on failure, so the caller can surface a
 * specific debug event (typo vs. wrong key shape) rather than a generic decrypt
 * error on every /v1/send.
 */
export function inspectServerPublicKey(b64: string): ServerPublicKeyValidation {
  let decoded: Uint8Array | null = null;
  try {
    const binary = atob(b64);
    decoded = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) decoded[i] = binary.charCodeAt(i);
  } catch {
    return { ok: false, reason: "invalid_base64", length: 0 };
  }
  if (decoded.length !== 32) {
    return { ok: false, reason: "wrong_byte_length", length: decoded.length };
  }
  return { ok: true, value: b64 };
}

const VALID_PRIORITIES: ReadonlySet<string> = new Set(["min", "low", "default", "high", "urgent"]);

// Defensive bounds: server clamps these at /v1/send ingress, but the parser
// applies them client-side too so a rogue token holder (or a future server bug)
// can't push oversized payloads into IDB / chrome.storage.
const MAX_STRING_LEN = 4096;
const MAX_ARRAY_LEN = 16;

/**
 * Parse and structurally validate a public-API plaintext envelope. Returns
 * null on any shape mismatch — the SW push handler / SSE consumer should
 * treat null as "drop and log" the same way the Android opener does on
 * invalid JSON or wrong type discriminator.
 */
export function parsePublicSendEnvelope(plaintext: string): PublicSendEnvelope | null {
  let raw: unknown;
  try {
    raw = JSON.parse(plaintext);
  } catch {
    return null;
  }
  if (!isObject(raw)) return null;
  if (raw.type !== "notification") return null;
  if (!isObject(raw.payload) || !isObject(raw.source)) return null;
  if (raw.source.kind !== "api") return null;

  const p = raw.payload;
  if (
    typeof p.message !== "string" ||
    p.message.length === 0 ||
    p.message.length > MAX_STRING_LEN
  ) {
    return null;
  }
  const priority =
    typeof p.priority === "string" && VALID_PRIORITIES.has(p.priority)
      ? (p.priority as PublicSendPriority)
      : "default";
  const tags = Array.isArray(p.tags)
    ? p.tags
        .filter((t): t is string => typeof t === "string" && t.length <= MAX_STRING_LEN)
        .slice(0, MAX_ARRAY_LEN)
    : [];
  const actions: PublicSendAction[] = Array.isArray(p.actions)
    ? p.actions
        .filter(isObject)
        .filter(
          (a): a is { label: string; url: string } =>
            typeof a.label === "string" &&
            a.label.length > 0 &&
            a.label.length <= MAX_STRING_LEN &&
            typeof a.url === "string" &&
            a.url.length <= MAX_STRING_LEN &&
            isHttpUrl(a.url)
        )
        .map((a) => ({ label: a.label, url: a.url }))
        .slice(0, MAX_ARRAY_LEN)
    : [];

  const s = raw.source;
  if (
    typeof s.token_id !== "string" ||
    s.token_id.length === 0 ||
    s.token_id.length > MAX_STRING_LEN ||
    typeof s.label !== "string" ||
    s.label.length === 0 ||
    s.label.length > MAX_STRING_LEN
  ) {
    return null;
  }
  // Sanity-cap at year 2286 (10-digit unix seconds) so a server bug or hostile
  // payload can't surface a year-9999 timestamp that sorts above every real
  // notification and confuses time-based UI.
  if (
    typeof s.received_at !== "number" ||
    !Number.isFinite(s.received_at) ||
    s.received_at <= 0 ||
    s.received_at > 9_999_999_999
  ) {
    return null;
  }

  const urlIsValid =
    typeof p.url === "string" && p.url.length <= MAX_STRING_LEN && isHttpUrl(p.url);
  const titleValid =
    typeof p.title === "string" && p.title.length > 0 && p.title.length <= MAX_STRING_LEN;
  const urlTitleValid =
    urlIsValid &&
    typeof p.url_title === "string" &&
    p.url_title.length > 0 &&
    p.url_title.length <= MAX_STRING_LEN;
  return {
    type: "notification",
    payload: {
      message: p.message,
      ...(titleValid ? { title: p.title as string } : {}),
      priority,
      tags,
      ...(urlIsValid ? { url: p.url as string } : {}),
      // Keep url_title only when url survives — an orphaned label has no
      // destination and would confuse a future URL renderer.
      ...(urlTitleValid ? { url_title: p.url_title as string } : {}),
      actions,
      markdown: typeof p.markdown === "boolean" ? p.markdown : false,
      // Clamp ttl to spec bounds (0..72h = 259_200s). Server clamps too, but
      // a future auto-dismiss consumer reads this value directly.
      ttl:
        typeof p.ttl === "number" && Number.isFinite(p.ttl)
          ? Math.min(Math.max(0, p.ttl), 259_200)
          : 0,
    },
    source: {
      kind: "api",
      token_id: s.token_id,
      label: s.label,
      received_at: s.received_at,
    },
  };
}

/**
 * Map a parsed public-API envelope into the existing DecryptedNotification
 * shape so the rest of the storage / render pipeline doesn't need to branch
 * on senderDeviceId === "server". Mirrors Android's PublicApiRenderer:
 *
 *  - title  → payload.title ?? source.label
 *  - body   → payload.message
 *  - app    → source.label  (shown as the app name in the row)
 *  - appPackage → "api:<token_id>" (group key parity with Android)
 *  - timestamp → source.received_at * 1000
 *
 * Public-API URL, url_title, actions, markdown, priority, and ttl map onto the
 * dedicated DecryptedNotification fields (url/urlTitle/linkActions/markdown/
 * priority/expiresAt). The envelope's `actions` ({label,url}) map to
 * `linkActions` — a separate field from the Android `actions` (RemoteInput/
 * button/activity), which has no analogue on a server-origin notification.
 * `ttl` is converted to an absolute `expiresAt` (receivedAt + ttl*1000) so the
 * auto-dismiss sweep needs no per-item base time. `tags` carry no render
 * surface and are intentionally not mapped.
 */
export function publicSendEnvelopeToNotification(
  envelope: PublicSendEnvelope,
  msgId: string,
  seqId: number,
  receivedAtMs: number = Date.now()
): DecryptedNotification {
  const { payload, source } = envelope;
  return {
    msgId,
    seqId,
    app: source.label,
    appPackage: `api:${source.token_id}`,
    title: payload.title ?? source.label,
    body: payload.message,
    timestamp: source.received_at * 1000,
    receivedAt: receivedAtMs,
    isRead: false,
    ...(payload.url ? { url: payload.url } : {}),
    ...(payload.url_title ? { urlTitle: payload.url_title } : {}),
    ...(payload.actions.length > 0 ? { linkActions: payload.actions } : {}),
    ...(payload.markdown ? { markdown: true } : {}),
    // Always set (unlike the sparse-optional fields above): the parser defaults
    // priority to "default", and a present-but-"default" value is meaningful —
    // it marks a server-origin notification (device-mirrored ones leave priority
    // undefined), which the accent styling keys off.
    priority: payload.priority,
    ...(payload.ttl > 0 ? { expiresAt: receivedAtMs + payload.ttl * 1000 } : {}),
  };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Action URLs are clicked from the OS toast; restrict to http(s) so a crafted
 *  `javascript:` / `data:` URL can't execute in a browser context. */
function isHttpUrl(s: string): boolean {
  try {
    const parsed = new URL(s);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}
